/**
 * Pagination utilities for handling standard MOS API responses
 * Based on docs/STANDARD_PAGINATION.md
 */

import type { PaginationMetadata } from '@/types/pagination';

export interface PaginationInfo {
  totalRecords: number;
  currentPage: number;
  perPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Extract pagination information from API response
 * Supports both standard and legacy response formats
 */
export function extractPaginationInfo(response: any): PaginationInfo | null {
  // Standard pagination format
  if (response.data?.pagination) {
    const pagination = response.data.pagination as PaginationMetadata & {
      page?: number;
      limit?: number;
      total?: number;
    };
    const totalRecords = pagination.total_records ?? pagination.total ?? 0;
    const perPage = pagination.per_page ?? pagination.limit ?? 20;
    const currentPage = pagination.current_page ?? pagination.page ?? 1;
    const totalPages = pagination.total_pages ?? Math.ceil(totalRecords / perPage);

    return {
      totalRecords,
      currentPage,
      perPage,
      totalPages,
      hasNext: pagination.has_next ?? currentPage < totalPages,
      hasPrevious: pagination.has_previous ?? currentPage > 1
    };
  }
  
  // Legacy format support
  if (response.data?.totalRecords !== undefined) {
    return {
      totalRecords: response.data.totalRecords,
      currentPage: response.data.currentPage || 1,
      perPage: response.data.perPage || response.data.pageSize || 20,
      totalPages: response.data.totalPages || Math.ceil(response.data.totalRecords / (response.data.perPage || response.data.pageSize || 20)),
      hasNext: response.data.hasNext || false,
      hasPrevious: response.data.hasPrevious || false
    };
  }
  
  return null;
}

/**
 * Get total records count from response
 * Supports both standard and legacy formats
 */
export function getTotalRecords(response: any): number {
  const paginationInfo = extractPaginationInfo(response);
  return paginationInfo?.totalRecords || 0;
}

/**
 * Check if response has more pages
 */
export function hasNextPage(response: any): boolean {
  const paginationInfo = extractPaginationInfo(response);
  return paginationInfo?.hasNext || false;
}

/**
 * Check if response has previous pages
 */
export function hasPreviousPage(response: any): boolean {
  const paginationInfo = extractPaginationInfo(response);
  return paginationInfo?.hasPrevious || false;
}

/**
 * Get current page from response
 */
export function getCurrentPage(response: any): number {
  const paginationInfo = extractPaginationInfo(response);
  return paginationInfo?.currentPage || 1;
}

/**
 * Validate if response follows standard pagination format
 */
export function isValidPaginationResponse(response: any): boolean {
  return response.data?.pagination !== undefined && 
         typeof response.data.pagination === 'object' &&
         typeof response.data.pagination.total_records === 'number';
}

/**
 * Convert TanStack Table sorting state to API parameters
 */
export function convertSortingToApiParams(sorting: Array<{ id: string; desc: boolean }>): { sort: string; order: 'asc' | 'desc' } {
  if (sorting.length === 0) {
    return { sort: 'id', order: 'asc' };
  }
  
  const firstSort = sorting[0];
  return {
    sort: firstSort.id,
    order: firstSort.desc ? 'desc' : 'asc'
  };
}

/**
 * Convert API sort parameters to TanStack Table sorting state
 */
export function convertApiParamsToSorting(sort: string, order: 'asc' | 'desc'): Array<{ id: string; desc: boolean }> {
  return [{ id: sort, desc: order === 'desc' }];
}

/**
 * Get default sort configuration for different entity types
 */
export function getDefaultSortConfig(entityType: string): { sort: string; order: 'asc' | 'desc' } {
  const defaults: Record<string, { sort: string; order: 'asc' | 'desc' }> = {
    students: { sort: 'last_name', order: 'asc' },
    teachers: { sort: 'last_name', order: 'asc' },
    courses: { sort: 'name', order: 'asc' },
    classes: { sort: 'name', order: 'asc' },
    grades: { sort: 'level', order: 'asc' },
    parents: { sort: 'last_name', order: 'asc' },
    exams: { sort: 'created_at', order: 'desc' },
    attendance: { sort: 'date', order: 'desc' },
    scores: { sort: 'created_at', order: 'desc' }
  };
  
  return defaults[entityType] || { sort: 'id', order: 'asc' };
} 
